(() => {
  if (customElements.get('bracelet-configurator')) {
    return;
  }

  const SELECTED_CLASS = 'is-selected';
  const prefetchedCollectionRoutes = new Set();
  const prefetchedCollectionLinks = new Set();
  const prefetchedCollectionDocuments = new Map();
  const preloadedCollectionAssets = new Set();

  class BraceletConfigurator extends HTMLElement {
    connectedCallback() {
      const dataScript = this.querySelector('[data-bracelet-configurator-data]');

      if (!dataScript) {
        return;
      }

      try {
        this.data = JSON.parse(dataScript.textContent);
      } catch (error) {
        this.showMessage('Configuration indisponible.');
        return;
      }

      this.families = Array.isArray(this.data.families) ? this.data.families.filter((family) => Array.isArray(family.cases) && family.cases.length) : [];
      this.bracelets = Array.isArray(this.data.bracelets) ? this.data.bracelets.filter((bracelet) => bracelet.variantId) : [];

      if (!this.families.length || !this.bracelets.length) {
        this.showMessage('Configuration incomplete.');
        return;
      }

      const currentProductId = String(this.data.currentProductId || '');
      const initialBraceletIndex = this.bracelets.findIndex((bracelet) => String(bracelet.productId) === currentProductId);

      this.state = {
        familyIndex: 0,
        caseIndex: 0,
        braceletIndex: initialBraceletIndex >= 0 ? initialBraceletIndex : 0,
        quantity: 1,
      };

      this.classList.toggle('is-single-bracelet', this.bracelets.length <= 1);

      this.els = {
        familyStrip: this.querySelector('[data-bc-family-strip]'),
        caseStrip: this.querySelector('[data-bc-case-strip]'),
        stage: this.querySelector('[data-bc-stage]'),
        prevImage: this.querySelector('[data-bc-prev-image]'),
        nextImage: this.querySelector('[data-bc-next-image]'),
        strapImage: this.querySelector('[data-bc-strap-image]'),
        caseImage: this.querySelector('[data-bc-case-image]'),
        title: this.querySelector('[data-bc-title]'),
        price: this.querySelector('[data-bc-price]'),
        mediaStrip: this.querySelector('[data-bc-media-strip]'),
        dots: this.querySelector('[data-bc-dots]'),
        selectionKicker: this.querySelector('[data-bc-selection-kicker]'),
        selectionTitle: this.querySelector('[data-bc-selection-title]'),
        selectionTotal: this.querySelector('[data-bc-selection-total]'),
        form: this.querySelector('[data-bc-form]'),
        variantInput: this.querySelector('[data-bc-variant-input]'),
        quantityInput: this.querySelector('[data-bc-quantity-input]'),
        addButton: this.querySelector('[data-bc-add]'),
        addButtonLabel: this.querySelector('[data-bc-add-label]'),
        addButtonSeparator: this.querySelector('[data-bc-add-separator]'),
        addButtonPrice: this.querySelector('[data-bc-add-price]'),
        addButtonShipping: this.querySelector('[data-bc-add-shipping]'),
        notifyShell: this.querySelector('[data-bc-notify-shell]'),
        notifyButton: this.querySelector('[data-bc-notify]'),
        notificationTitle: this.querySelector('product-notification .product__notification__title'),
        notificationVariant: this.querySelector('product-notification [data-variant-title]'),
        notificationProduct: this.querySelector('product-notification [data-notification-product]'),
        message: this.querySelector('[data-bc-message]'),
      };

      this.onClick = this.onClick.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onTouchStart = this.onTouchStart.bind(this);
      this.onTouchEnd = this.onTouchEnd.bind(this);
      this.onMediaScroll = this.onMediaScroll.bind(this);
      this.onCollectionIntent = this.onCollectionIntent.bind(this);

      this.addEventListener('click', this.onClick);
      this.addEventListener('pointerover', this.onCollectionIntent);
      this.addEventListener('focusin', this.onCollectionIntent);
      this.addEventListener('touchstart', this.onCollectionIntent, {passive: true});
      this.els.form?.addEventListener('submit', this.onSubmit);
      this.els.stage?.addEventListener('touchstart', this.onTouchStart, {passive: true});
      this.els.stage?.addEventListener('touchend', this.onTouchEnd, {passive: true});
      this.els.mediaStrip?.addEventListener('scroll', this.onMediaScroll, {passive: true});

      this.renderStaticControls();
      this.render();
      this.finishInitialReveal();
      this.preloadCurrentFamilyCases();
      this.scheduleCollectionPrefetch();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('pointerover', this.onCollectionIntent);
      this.removeEventListener('focusin', this.onCollectionIntent);
      this.removeEventListener('touchstart', this.onCollectionIntent);
      this.els?.form?.removeEventListener('submit', this.onSubmit);
      this.els?.stage?.removeEventListener('touchstart', this.onTouchStart);
      this.els?.stage?.removeEventListener('touchend', this.onTouchEnd);
      this.els?.mediaStrip?.removeEventListener('scroll', this.onMediaScroll);
      window.clearTimeout(this.slideAnimationTimer);
      window.clearTimeout(this.softSwapAnimationTimer);
      window.clearTimeout(this.softEnterTimer);
      window.clearTimeout(this.collectionNavigationTimer);
      this.collectionNavigationPending = false;
      this.cancelCollectionPrefetch();
    }

    get selectedFamily() {
      return this.families[this.state.familyIndex] || this.families[0];
    }

    get selectedCase() {
      const cases = this.selectedFamily?.cases || [];
      return cases[this.state.caseIndex] || cases[0] || {};
    }

    get selectedBracelet() {
      return this.bracelets[this.state.braceletIndex] || this.bracelets[0] || {};
    }

    renderStaticControls() {
      this.renderFamilies();
      this.renderCases();
    }

    renderFamilies() {
      if (!this.els.familyStrip) return;

      const showFamilySelector = this.data.settings?.showFamilySelector !== false && this.families.length > 1;
      this.els.familyStrip.hidden = !showFamilySelector;

      if (!showFamilySelector) {
        this.els.familyStrip.innerHTML = '';
        return;
      }

      this.els.familyStrip.innerHTML = this.families
        .map((family, index) => {
          const selected = index === this.state.familyIndex ? ` ${SELECTED_CLASS}` : '';
          return `
            <button class="bc__family-button${selected}" type="button" data-bc-family="${index}">
              ${this.escapeHTML(family.name)}
            </button>
          `;
        })
        .join('');
    }

    renderCases() {
      if (!this.els.caseStrip) return;

      const cases = this.selectedFamily?.cases || [];
      this.els.caseStrip.hidden = cases.length <= 1 && this.data.settings?.hideSingleCase === true;

      this.els.caseStrip.innerHTML = cases
        .map((watchCase, index) => {
          const selected = index === this.state.caseIndex ? ` ${SELECTED_CLASS}` : '';
          const thumb = watchCase.thumbnail || watchCase.image;
          const visibleDetails = this.getCaseVisibleDetails(watchCase);
          const thumbMarkup = thumb
            ? `<img src="${this.escapeAttribute(thumb)}" alt="${this.escapeAttribute(watchCase.name)}" loading="lazy">`
            : '<span class="bc__case-empty" aria-hidden="true"></span>';
          const copyMarkup = visibleDetails
            .map((detail, detailIndex) => {
              const className = detailIndex === 0 ? 'bc__case-name' : 'bc__case-color';
              return `<span class="${className}">${this.escapeHTML(detail.value)}</span>`;
            })
            .join('');

          return `
            <button class="bc__case-button${selected}" type="button" data-bc-case="${index}" aria-label="${this.escapeAttribute(watchCase.name || 'Boitier')}">
              <span class="bc__case-thumb">${thumbMarkup}</span>
              ${copyMarkup ? `<span class="bc__case-copy">${copyMarkup}</span>` : ''}
            </button>
          `;
        })
        .join('');
    }

    render(slideDirection = 0, options = {}) {
      const bracelet = this.selectedBracelet;
      const watchCase = this.selectedCase;
      const prevBracelet = this.bracelets[this.wrapIndex(this.state.braceletIndex - 1, this.bracelets.length)];
      const nextBracelet = this.bracelets[this.wrapIndex(this.state.braceletIndex + 1, this.bracelets.length)];
      const braceletImage = bracelet.configuratorImage || bracelet.featuredImage || '';
      const prevImage = prevBracelet?.configuratorImage || prevBracelet?.featuredImage || '';
      const nextImage = nextBracelet?.configuratorImage || nextBracelet?.featuredImage || '';

      this.setImage(this.els.strapImage, braceletImage, bracelet.title);
      this.setImage(this.els.prevImage, prevImage, prevBracelet?.title || '');
      this.setImage(this.els.nextImage, nextImage, nextBracelet?.title || '');

      if (watchCase.image) {
        this.setImage(this.els.caseImage, watchCase.image, watchCase.name);
        this.els.caseImage.hidden = false;
      } else if (this.els.caseImage) {
        this.els.caseImage.hidden = true;
      }

      if (this.els.title) this.els.title.textContent = bracelet.title || '';
      if (this.els.price) this.els.price.innerHTML = this.renderPrice(bracelet);

      if (options.renderCases === true) {
        this.renderCases();
      } else {
        this.updateCaseButtons();
      }

      this.updateFamilyButtons();
      if (options.renderMedia !== false) {
        this.renderMedia();
      }
      this.syncForm();
      this.preloadNeighbors();

      if (slideDirection) {
        this.animateSlide(slideDirection);
      } else if (options.animateSwap === true) {
        this.animateSoftSwap();
      }
    }

    renderMedia() {
      if (!this.els.mediaStrip) return;

      const bracelet = this.selectedBracelet;
      const items = this.getActiveMediaItems(bracelet);
      const total = items.length;

      this.activeMediaItems = items;
      this.els.mediaStrip.innerHTML = items.map((media, index) => this.renderMediaCard(media, index, total)).join('');
      this.els.mediaStrip.scrollLeft = 0;
      this.bindInlineVideos();
      this.renderDots(0);
    }

    renderDots(activeIndex = 0) {
      if (!this.els.dots) return;

      const count = Array.isArray(this.activeMediaItems) ? this.activeMediaItems.length : 0;
      const maxDots = Math.min(count, 8);

      if (maxDots <= 1) {
        this.els.dots.innerHTML = '';
        return;
      }

      const activeRatio = count <= maxDots ? activeIndex : Math.round((activeIndex / (count - 1)) * (maxDots - 1));

      this.els.dots.innerHTML = Array.from({length: maxDots})
        .map((_, index) => `<span class="bc__dot${index === activeRatio ? ` ${SELECTED_CLASS}` : ''}" aria-hidden="true"></span>`)
        .join('');
    }

    updateMediaDots(activeIndex) {
      if (!this.els.dots) return;

      const dots = Array.from(this.els.dots.querySelectorAll('.bc__dot'));
      if (!dots.length) return;

      const count = Array.isArray(this.activeMediaItems) ? this.activeMediaItems.length : dots.length;
      const dotIndex = count <= dots.length ? activeIndex : Math.round((activeIndex / (count - 1)) * (dots.length - 1));

      dots.forEach((dot, index) => dot.classList.toggle(SELECTED_CLASS, index === Math.min(dotIndex, dots.length - 1)));
    }

    getCaseVisibleDetails(watchCase) {
      const settings = this.data.settings || {};
      const details = [];
      var _isEnDet = (document.documentElement.lang || '').toLowerCase().indexOf('en') === 0;

      if (settings.showCaseName !== false && watchCase.name) {
        details.push({label: _isEnDet ? 'Name' : 'Nom', value: watchCase.name});
      }

      if (settings.showCaseCollection === true && watchCase.collectionName) {
        details.push({label: 'Collection', value: watchCase.collectionName});
      }

      if (settings.showCaseReference === true && watchCase.reference) {
        details.push({label: _isEnDet ? 'Reference' : 'Référence', value: watchCase.reference});
      }

      if (settings.showCaseColor !== false && watchCase.color) {
        details.push({label: _isEnDet ? 'Color' : 'Coloris', value: watchCase.color});
      }

      return details;
    }

    getActiveMediaItems(bracelet = this.selectedBracelet) {
      const media = Array.isArray(bracelet.media) ? bracelet.media : [];
      const seen = new Set();

      return media.filter((item) => {
        if (!item?.src && !item?.poster) return false;

        const key = this.getMediaKey(item);
        if (seen.has(key)) return false;

        seen.add(key);
        return true;
      });
    }

    renderMediaCard(media, index, total) {
      const count = `${index + 1}/${total}`;
      const label = media.alt || media.label || media.caseTitle || media.braceletTitle || 'Media';

      if (media.type === 'video') {
        return `
          <div class="bc__media-card bc__media-card--video is-paused" data-bc-media-index="${index}" aria-label="${this.escapeAttribute(`Lire ${label}`)}">
            <span class="bc__media-count">${count}</span>
            ${this.renderMediaVisual(media, {thumbnail: true, inlineVideo: true})}
          </div>
        `;
      }

      return `
        <button class="bc__media-card bc__media-card--image" type="button" data-bc-media-index="${index}" data-bc-open-media aria-label="${this.escapeAttribute(`Ouvrir ${label}`)}">
          <span class="bc__media-count">${count}</span>
          ${this.renderMediaVisual(media, {thumbnail: true})}
        </button>
      `;
    }

    renderMediaVisual(media, options = {}) {
      if (media.type === 'combo') {
        return `
          <div class="bc__media-combo">
            <div class="bc__media-combo-stack">
              ${media.braceletImage ? `<img src="${this.escapeAttribute(media.braceletImage)}" alt="${this.escapeAttribute(media.braceletTitle)}" loading="${options.thumbnail ? 'lazy' : 'eager'}">` : ''}
              ${media.caseImage ? `<img src="${this.escapeAttribute(media.caseImage)}" alt="${this.escapeAttribute(media.caseTitle)}" loading="${options.thumbnail ? 'lazy' : 'eager'}">` : ''}
            </div>
          </div>
        `;
      }

      if (media.type === 'video' && media.src) {
        const poster = media.poster ? ` poster="${this.escapeAttribute(media.poster)}"` : '';
        const controls = options.inlineVideo || !options.thumbnail ? ' controls' : '';
        const inlineAttribute = options.inlineVideo ? ' data-bc-inline-video' : '';

        const label = media.alt || media.label || 'la video';

        return `
          <span class="bc__media-video-frame">
            <video${inlineAttribute}${controls} playsinline preload="metadata"${poster}>
              <source src="${this.escapeAttribute(media.src)}" type="video/mp4">
            </video>
            <button class="bc__media-play" type="button" data-bc-video-play aria-label="${this.escapeAttribute(`Lire ${label}`)}"><span aria-hidden="true"></span></button>
          </span>
        `;
      }

      const src = media.src || media.poster;
      if (!src) return '<span class="bc__media-empty" aria-hidden="true"></span>';

      return `<img src="${this.escapeAttribute(src)}" alt="${this.escapeAttribute(media.alt || media.label || '')}" loading="${options.thumbnail ? 'lazy' : 'eager'}">`;
    }

    updateFamilyButtons() {
      this.els.familyStrip?.querySelectorAll('[data-bc-family]').forEach((button) => {
        button.classList.toggle(SELECTED_CLASS, Number(button.dataset.bcFamily) === this.state.familyIndex);
      });
    }

    updateCaseButtons() {
      this.els.caseStrip?.querySelectorAll('[data-bc-case]').forEach((button) => {
        button.classList.toggle(SELECTED_CLASS, Number(button.dataset.bcCase) === this.state.caseIndex);
      });
    }

    bindInlineVideos() {
      const videos = Array.from(this.els.mediaStrip?.querySelectorAll('[data-bc-inline-video]') || []);

      videos.forEach((video) => {
        const card = video.closest('.bc__media-card--video');
        const syncState = () => {
          card?.classList.toggle('is-paused', video.paused || video.ended);
        };
        const markStarted = () => {
          card?.classList.add('has-started');
          syncState();
        };
        const markEnded = () => {
          card?.classList.remove('has-started');
          syncState();
        };

        syncState();
        video.addEventListener('play', markStarted);
        video.addEventListener('pause', syncState);
        video.addEventListener('ended', markEnded);
      });
    }

    syncForm() {
      const bracelet = this.selectedBracelet;
      const family = this.selectedFamily;
      const quantity = 1;
      const available = bracelet.available !== false;
      const buttonPrice = bracelet.priceFormatted || this.formatMoney(bracelet.price || 0);
      const hasNotify = Boolean(this.els.notifyShell);

      this.state.quantity = quantity;

      if (this.els.variantInput) this.els.variantInput.value = bracelet.variantId || '';
      if (this.els.quantityInput) this.els.quantityInput.value = String(quantity);

      if (this.els.selectionKicker) {
        this.els.selectionKicker.textContent = family?.name || '';
      }

      if (this.els.selectionTitle) {
        this.els.selectionTitle.textContent = bracelet.title || '';
      }

      if (this.els.selectionTotal) {
        this.els.selectionTotal.textContent = buttonPrice;
      }

      if (this.els.addButton) {
        this.els.addButton.disabled = !available;
        this.els.addButton.classList.toggle('bc__add--with-shipping', available && Boolean(this.els.addButtonShipping));
      }

      var _isEn = (document.documentElement.lang || '').toLowerCase().indexOf('en') === 0;
      var _addLabel = _isEn ? 'Add to cart' : 'Ajouter au panier';
      var _unavailLabel = _isEn ? 'Unavailable' : 'Indisponible';

      if (this.els.addButtonLabel) {
        this.els.addButtonLabel.textContent = available ? _addLabel : _unavailLabel;
      }

      if (this.els.addButtonPrice) {
        this.els.addButtonPrice.textContent = buttonPrice;
        this.els.addButtonPrice.hidden = !available;
      } else if (this.els.addButton) {
        this.els.addButton.textContent = available ? `${_addLabel} | ${buttonPrice}` : _unavailLabel;
      }

      if (this.els.addButtonSeparator) {
        this.els.addButtonSeparator.hidden = !available;
      }

      if (this.els.addButtonShipping) {
        this.els.addButtonShipping.hidden = !available;
      }

      if (this.els.form) {
        this.els.form.hidden = !available && hasNotify;
      }

      if (this.els.notifyShell) {
        this.els.notifyShell.hidden = available;
      }

      this.syncNotificationForm(bracelet);
    }

    syncNotificationForm(bracelet) {
      const title = bracelet.title || '';
      const variantTitle = bracelet.variantTitle || '';
      const hasNamedVariant = variantTitle && variantTitle !== 'Default Title';

      if (this.els.notificationTitle) {
        this.els.notificationTitle.textContent = title;
      }

      if (this.els.notificationVariant) {
        this.els.notificationVariant.textContent = hasNamedVariant ? variantTitle : '';
        this.els.notificationVariant.hidden = !hasNamedVariant;
      }

      if (this.els.notificationProduct) {
        this.els.notificationProduct.value = bracelet.notificationProduct || [title, hasNamedVariant ? variantTitle : ''].filter(Boolean).join(' - ');
      }
    }

    onClick(event) {
      const collectionLink = event.target.closest('[data-bc-collection-link]');
      const mediaButton = event.target.closest('[data-bc-open-media]');
      const videoPlayButton = event.target.closest('[data-bc-video-play]');
      const familyButton = event.target.closest('[data-bc-family]');
      const caseButton = event.target.closest('[data-bc-case]');
      const slideButton = event.target.closest('[data-bc-slide]');

      if (collectionLink) {
        this.prepareCollectionNavigation(event, collectionLink);
        return;
      }

      if (videoPlayButton) {
        event.preventDefault();
        this.playInlineVideo(videoPlayButton);
        return;
      }

      if (mediaButton) {
        this.openNativeMediaViewer(Number(mediaButton.dataset.bcMediaIndex) || 0);
        return;
      }

      if (familyButton) {
        this.state.familyIndex = Number(familyButton.dataset.bcFamily) || 0;
        this.state.caseIndex = 0;
        this.renderFamilies();
        this.render(0, {renderCases: true, renderMedia: false, animateSwap: true});
        this.preloadCurrentFamilyCases();
        return;
      }

      if (caseButton) {
        this.state.caseIndex = Number(caseButton.dataset.bcCase) || 0;
        this.render(0, {renderCases: false, renderMedia: false, animateSwap: true});
        return;
      }

      if (slideButton) {
        this.slideBracelet(Number(slideButton.dataset.bcSlide) || 1);
      }
    }

    onCollectionIntent(event) {
      const link = event.target.closest('[data-bc-collection-link]');

      if (!link || !this.contains(link)) return;

      this.prefetchCollectionRoute(link.href, {eager: true});
    }

    prepareCollectionNavigation(event, link) {
      if (
        event.defaultPrevented ||
        event.button > 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        link.hasAttribute('download') ||
        (link.target && link.target !== '_self')
      ) {
        return;
      }

      const url = this.getSameOriginUrl(link.href);
      if (!url || url.href === window.location.href) return;

      event.preventDefault();

      if (this.collectionNavigationPending) return;

      this.collectionNavigationPending = true;

      this.navigateCollectionRoute(url, link);
    }

    async navigateCollectionRoute(url, link) {
      try {
        this.prepareCollectionTransition(link, url.href);
        const doc = await this.fetchCollectionDocument(url.href);
        const incomingConfigurator = doc.querySelector('bracelet-configurator');

        if (!incomingConfigurator) {
          throw new Error('Target configurator missing');
        }

        const swapConfigurator = () => {
          this.syncDocumentMetadata(doc);
          this.pushPartialNavigationState(url.href);
          this.replaceWith(incomingConfigurator);
        };

        if (this.canUseSameDocumentViewTransition()) {
          const transition = document.startViewTransition(swapConfigurator);
          await transition.finished.catch(() => {});
        } else {
          swapConfigurator();
        }
      } catch (error) {
        window.location.assign(url.href);
      }
    }

    prepareCollectionTransition(link, href) {
      this.prefetchCollectionRoute(href, {eager: true});
      this.classList.add('is-collection-switching');
      this.setAttribute('aria-busy', 'true');

      this.querySelectorAll('.bc__collection-tab').forEach((tab) => {
        tab.classList.remove(SELECTED_CLASS);
        tab.removeAttribute('aria-current');
      });

      link.classList.add(SELECTED_CLASS);
      link.setAttribute('aria-current', 'page');
    }

    async fetchCollectionDocument(href) {
      let htmlRequest = prefetchedCollectionDocuments.get(href);

      if (!htmlRequest) {
        htmlRequest = this.requestCollectionHTML(href);
        prefetchedCollectionDocuments.set(href, htmlRequest);
      }

      const html = await htmlRequest;

      if (!html || typeof DOMParser !== 'function') {
        throw new Error('Route document unavailable');
      }

      return new DOMParser().parseFromString(html, 'text/html');
    }

    async requestCollectionHTML(href, options = {}) {
      const response = await window.fetch(href, {
        credentials: 'same-origin',
        cache: options.cache || 'default',
      });

      if (!response.ok) {
        throw new Error(`Route fetch failed: ${response.status}`);
      }

      return response.text();
    }

    canUseSameDocumentViewTransition() {
      if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return false;

      return typeof document.startViewTransition === 'function';
    }

    syncDocumentMetadata(doc) {
      const title = doc.querySelector('title')?.textContent;

      if (title) {
        document.title = title;
      }

      [
        'link[rel="canonical"]',
        'meta[property="og:url"]',
        'meta[property="og:title"]',
        'meta[name="twitter:title"]',
      ].forEach((selector) => this.replaceHeadElement(doc, selector));
    }

    replaceHeadElement(doc, selector) {
      const incoming = doc.head?.querySelector(selector);
      const current = document.head?.querySelector(selector);

      if (!incoming || !current) return;

      current.replaceWith(incoming.cloneNode(true));
    }

    pushPartialNavigationState(href) {
      BraceletConfigurator.partialNavigationUsed = true;
      BraceletConfigurator.installPopstateReload();
      window.history.pushState({braceletConfigurator: true}, '', href);
    }

    static installPopstateReload() {
      if (BraceletConfigurator.hasPopstateReload) return;

      BraceletConfigurator.hasPopstateReload = true;

      window.addEventListener('popstate', () => {
        if (!BraceletConfigurator.partialNavigationUsed) return;

        window.location.reload();
      });
    }

    playInlineVideo(trigger) {
      const card = trigger.closest('.bc__media-card--video');
      const video = card?.querySelector('[data-bc-inline-video]');

      if (!video) return;

      video.controls = true;

      if (typeof video.play !== 'function') return;

      const playResult = video.play();
      if (playResult && typeof playResult.catch === 'function') {
        playResult.catch(() => {
          card?.classList.add('is-paused');
        });
      }
    }

    onMediaScroll() {
      if (this.mediaScrollFrame) return;

      this.mediaScrollFrame = window.requestAnimationFrame(() => {
        this.mediaScrollFrame = null;
        this.updateMediaDots(this.getNearestMediaIndex());
      });
    }

    onSubmit(event) {
      event.preventDefault();
      this.syncForm();
      this.showMessage('');

      if (!this.selectedBracelet.variantId || this.selectedBracelet.available === false) {
        this.showMessage('Ce bracelet est indisponible.');
        return;
      }

      if (!window.theme?.routes?.cart_add_url) {
        this.els.form?.submit();
        return;
      }

      document.dispatchEvent(
        new CustomEvent('theme:cart:add', {
          detail: {
            button: this.els.addButton,
          },
          bubbles: false,
        })
      );
    }

    onTouchStart(event) {
      this.touchStartX = event.changedTouches?.[0]?.clientX || 0;
    }

    onTouchEnd(event) {
      const endX = event.changedTouches?.[0]?.clientX || 0;
      const deltaX = endX - (this.touchStartX || 0);

      if (Math.abs(deltaX) < 42) return;

      this.slideBracelet(deltaX < 0 ? 1 : -1);
    }

    slideBracelet(direction) {
      if (this.bracelets.length <= 1) return;

      const slideDirection = direction < 0 ? -1 : 1;
      this.state.braceletIndex = this.wrapIndex(this.state.braceletIndex + direction, this.bracelets.length);
      this.render(slideDirection, {renderCases: false});
    }

    animateSlide(direction) {
      if (!direction || !this.els.stage) return;

      const className = direction < 0 ? 'is-sliding-prev' : 'is-sliding-next';
      this.els.stage.classList.remove('is-sliding-prev', 'is-sliding-next');
      void this.els.stage.offsetWidth;
      this.els.stage.classList.add(className);
      window.clearTimeout(this.slideAnimationTimer);
      this.slideAnimationTimer = window.setTimeout(() => {
        this.els.stage?.classList.remove('is-sliding-prev', 'is-sliding-next');
      }, 460);
    }

    animateSoftSwap() {
      if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

      this.classList.remove('is-soft-swapping');
      void this.offsetWidth;
      this.classList.add('is-soft-swapping');

      window.clearTimeout(this.softSwapAnimationTimer);
      this.softSwapAnimationTimer = window.setTimeout(() => {
        this.classList.remove('is-soft-swapping');
      }, 280);
    }

    finishInitialReveal() {
      if (!this.classList.contains('is-soft-entering')) return;

      window.clearTimeout(this.softEnterTimer);
      this.softEnterTimer = window.setTimeout(() => {
        this.classList.remove('is-soft-entering');
      }, 320);
    }

    openNativeMediaViewer(index) {
      const items = this.buildPhotoswipeItems();
      const photoswipeIndex = items.findIndex((item) => item.sourceIndex === index);

      if (!items[photoswipeIndex]) return;

      if (!this.ensurePhotoswipeRuntime()) {
        this.showMessage('Visionneuse media indisponible.');
        return;
      }

      const optionClasses = ['pswp-zoom-gallery'];
      if (items.length === 1) optionClasses.push('pswp-zoom-gallery--single');
      if (items.some((item) => item.type === 'video')) optionClasses.push('pswp-zoom-gallery--mixed');

      const colorScheme = this.getColorScheme();
      if (colorScheme) optionClasses.push(colorScheme);

      const trigger = this.els.mediaStrip?.querySelector(`[data-bc-media-index="${index}"]`);
      const options = {
        history: false,
        focus: false,
        index: photoswipeIndex,
        mainClass: optionClasses.join(' '),
        showHideOpacity: true,
        howAnimationDuration: 150,
        hideAnimationDuration: 250,
        closeOnScroll: false,
        closeOnVerticalDrag: false,
        captionEl: true,
        closeEl: true,
        closeElClasses: ['caption-close', 'title'],
        tapToClose: false,
        clickToCloseNonZoomable: false,
        maxSpreadZoom: 2,
        loop: true,
        spacing: 0,
        allowPanToNext: true,
        pinchToClose: false,
        addCaptionHTMLFn: (item, captionEl) => {
          this.renderPhotoswipeCaption(item, captionEl);
          return false;
        },
        getThumbBoundsFn: () => this.getThumbBounds(trigger),
      };

      new window.theme.LoadPhotoswipe(items, options);
      this.renderPhotoswipeThumbs(items, photoswipeIndex);
      window.a11y.lastElement = trigger || this;
    }

    ensurePhotoswipeRuntime() {
      window.theme = window.theme || {};
      window.theme.assets = window.theme.assets || {};
      window.theme.assets.photoswipe = window.theme.assets.photoswipe || this.data?.assets?.photoswipe || '';
      window.theme.a11y = window.theme.a11y || {
        trapFocus() {},
        removeTrapFocus() {},
        autoFocusLastElement() {},
      };
      window.a11y = window.a11y || {lastElement: null};

      return typeof window.theme.LoadPhotoswipe === 'function' && Boolean(window.theme.assets.photoswipe);
    }

    buildPhotoswipeItems() {
      const mediaItems = Array.isArray(this.activeMediaItems) ? this.activeMediaItems : [];

      return mediaItems
        .map((media, sourceIndex) => {
          const src = media.src || media.poster;
          if (!src) return null;

          const title = this.selectedBracelet.title || '';
          const item = {
            title,
            msrc: media.thumb || media.poster || src,
            thumb: media.thumb || media.poster || src,
            sourceIndex,
            type: media.type || 'image',
          };

          if (media.type === 'video') {
            if (!media.src) return null;

            item.html = this.renderPhotoswipeVideo(media, title);
            item.w = Number.parseInt(media.width, 10) || 1600;
            item.h = Number.parseInt(media.height, 10) || 900;
            return item;
          }

          item.src = src;
          item.w = Number.parseInt(media.width, 10) || 1600;
          item.h = Number.parseInt(media.height, 10) || 1600;
          return item;
        })
        .filter(Boolean);
    }

    renderPhotoswipeVideo(media, title) {
      const poster = media.poster ? ` poster="${this.escapeAttribute(media.poster)}"` : '';

      return `
        <div class="pswp__custom-inner bc__pswp-video">
          <div class="pswp__custom-content">
            <div class="pswp__custom-native">
              <video controls playsinline preload="metadata"${poster} aria-label="${this.escapeAttribute(title)}">
                <source src="${this.escapeAttribute(media.src)}" type="video/mp4">
              </video>
            </div>
          </div>
        </div>
      `;
    }

    renderPhotoswipeCaption(item, captionEl) {
      const caption = captionEl?.children?.[0];
      if (!caption) return;

      caption.innerHTML = this.escapeHTML(item.title || this.selectedBracelet.title || '');
    }

    renderPhotoswipeThumbs(items, activeIndex = 0) {
      const thumbs = document.querySelector('.pswp__thumbs');
      if (!thumbs) return;

      thumbs.innerHTML = items
        .map((item, index) => this.renderPhotoswipeThumb(item, index === activeIndex))
        .join('');
    }

    renderPhotoswipeThumb(item, isCurrent = false) {
      const thumb = item.thumb || item.msrc || item.src || '';
      const currentClass = isCurrent ? ' is-current' : '';
      const label = item.type === 'video' ? `Video - ${item.title || ''}` : item.title || '';

      if (item.type === 'video') {
        return `
          <span class="pswp__thumb bc__pswp-thumb bc__pswp-thumb--video${currentClass}" aria-label="${this.escapeAttribute(label)}" aria-current="${isCurrent ? 'true' : 'false'}">
            ${thumb ? `<img src="${this.escapeAttribute(thumb)}" alt="${this.escapeAttribute(label)}" loading="lazy">` : '<span class="bc__pswp-thumb-placeholder" aria-hidden="true"></span>'}
            <span class="bc__pswp-thumb-play" aria-hidden="true"></span>
          </span>
        `;
      }

      return `<img class="pswp__thumb${currentClass}" src="${this.escapeAttribute(thumb)}" alt="${this.escapeAttribute(label)}" loading="lazy" aria-current="${isCurrent ? 'true' : 'false'}">`;
    }

    getThumbBounds(trigger) {
      const target = trigger?.querySelector('img, video') || trigger;

      if (!target) return null;

      const pageYScroll = window.scrollY || document.documentElement.scrollTop;
      const rect = target.getBoundingClientRect();

      return {
        x: rect.left,
        y: rect.top + pageYScroll,
        w: rect.width,
      };
    }

    getColorScheme() {
      const productInfo = this.closest('product-info');
      if (!productInfo) return '';

      return Array.from(productInfo.classList).find((className) => className.startsWith('color-scheme')) || '';
    }

    getNearestMediaIndex() {
      if (!this.els.mediaStrip) return 0;

      const cards = Array.from(this.els.mediaStrip.querySelectorAll('[data-bc-media-index]'));
      if (!cards.length) return 0;

      const stripLeft = this.els.mediaStrip.getBoundingClientRect().left;
      let nearestIndex = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;

      cards.forEach((card, index) => {
        const distance = Math.abs(card.getBoundingClientRect().left - stripLeft);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });

      return nearestIndex;
    }

    getMediaKey(media) {
      const src = media?.src || media?.poster || media?.thumb || '';
      return String(src).replace(/([?&])width=\d+(&?)/, '$1').replace(/[?&]$/, '');
    }

    setImage(image, src, alt) {
      if (!image) return;

      if (!src) {
        image.removeAttribute('src');
        image.alt = '';
        return;
      }

      if (image.getAttribute('src') !== src) {
        image.src = src;
      }

      image.alt = alt || '';
    }

    preloadNeighbors() {
      const indexes = [
        this.state.braceletIndex,
        this.wrapIndex(this.state.braceletIndex - 1, this.bracelets.length),
        this.wrapIndex(this.state.braceletIndex + 1, this.bracelets.length),
      ];

      indexes.forEach((index) => {
        const bracelet = this.bracelets[index];
        const src = bracelet?.configuratorImage || bracelet?.featuredImage;

        this.preloadImage(src);
      });
    }

    preloadCurrentFamilyCases() {
      const cases = Array.isArray(this.selectedFamily?.cases) ? this.selectedFamily.cases : [];

      cases.slice(0, 10).forEach((watchCase) => {
        this.preloadImage(watchCase?.thumbnail || watchCase?.image);
        this.preloadImage(watchCase?.image);
      });
    }

    scheduleCollectionPrefetch() {
      const links = this.getCollectionLinks();
      if (!links.length) return;

      const prefetch = () => {
        this.collectionPrefetchHandle = null;
        links.forEach((link) => this.prefetchCollectionRoute(link.href));
      };

      if (typeof window.requestIdleCallback === 'function') {
        this.collectionPrefetchType = 'idle';
        this.collectionPrefetchHandle = window.requestIdleCallback(prefetch, {timeout: 1600});
      } else {
        this.collectionPrefetchType = 'timeout';
        this.collectionPrefetchHandle = window.setTimeout(prefetch, 700);
      }
    }

    cancelCollectionPrefetch() {
      if (!this.collectionPrefetchHandle) return;

      if (this.collectionPrefetchType === 'idle' && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(this.collectionPrefetchHandle);
      } else {
        window.clearTimeout(this.collectionPrefetchHandle);
      }

      this.collectionPrefetchHandle = null;
    }

    getCollectionLinks() {
      return Array.from(this.querySelectorAll('[data-bc-collection-link]')).filter((link) => link.href);
    }

    prefetchCollectionRoute(rawUrl, options = {}) {
      const url = this.getSameOriginUrl(rawUrl);
      if (!url) return;

      this.appendCollectionPrefetchLink(url.href, options.eager === true);

      if (prefetchedCollectionRoutes.has(url.href)) return;
      prefetchedCollectionRoutes.add(url.href);

      const htmlRequest = this.requestCollectionHTML(url.href, {cache: 'force-cache'})
        .then((html) => {
          if (!html) {
            prefetchedCollectionDocuments.delete(url.href);
            return '';
          }

          this.preloadCollectionAssetsFromHTML(html);
          return html;
        })
        .catch(() => {
          prefetchedCollectionDocuments.delete(url.href);
          return '';
        });

      prefetchedCollectionDocuments.set(url.href, htmlRequest);
    }

    appendCollectionPrefetchLink(href, eager = false) {
      if (prefetchedCollectionLinks.has(href) || !document.head) return;

      prefetchedCollectionLinks.add(href);

      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.as = 'document';
      link.href = href;

      if ('fetchPriority' in link) {
        link.fetchPriority = eager ? 'high' : 'low';
      }

      document.head.appendChild(link);
    }

    preloadCollectionAssetsFromHTML(html) {
      if (!html || typeof DOMParser !== 'function') return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const dataScript = doc.querySelector('[data-bracelet-configurator-data]');

      if (!dataScript) return;

      try {
        this.preloadConfiguratorDataImages(JSON.parse(dataScript.textContent));
      } catch (error) {}
    }

    preloadConfiguratorDataImages(data) {
      const bracelets = Array.isArray(data?.bracelets) ? data.bracelets : [];
      const families = Array.isArray(data?.families) ? data.families : [];
      const currentProductId = String(data?.currentProductId || '');

      if (bracelets.length) {
        let activeIndex = bracelets.findIndex((bracelet) => String(bracelet.productId) === currentProductId);
        if (activeIndex < 0) activeIndex = 0;

        [
          activeIndex,
          this.wrapIndex(activeIndex - 1, bracelets.length),
          this.wrapIndex(activeIndex + 1, bracelets.length),
        ].forEach((index) => {
          const bracelet = bracelets[index];

          this.preloadImage(bracelet?.configuratorImage || bracelet?.featuredImage);
          this.getActiveMediaItems(bracelet)
            .slice(0, 2)
            .forEach((media) => this.preloadMediaImage(media));
        });
      }

      const cases = Array.isArray(families[0]?.cases) ? families[0].cases : [];
      cases.slice(0, 8).forEach((watchCase, index) => {
        this.preloadImage(watchCase?.thumbnail || watchCase?.image);
        if (index === 0) this.preloadImage(watchCase?.image);
      });
    }

    preloadMediaImage(media) {
      if (!media) return;

      if (media.type === 'combo') {
        this.preloadImage(media.braceletImage);
        this.preloadImage(media.caseImage);
        return;
      }

      this.preloadImage(media.thumb || media.poster || media.src);
    }

    preloadImage(src) {
      if (!src || preloadedCollectionAssets.has(src)) return;

      preloadedCollectionAssets.add(src);

      const image = new Image();
      image.decoding = 'async';
      image.src = src;
    }

    getSameOriginUrl(rawUrl) {
      try {
        const url = new URL(rawUrl, window.location.href);
        if (url.origin !== window.location.origin) return null;
        url.hash = '';
        return url;
      } catch (error) {
        return null;
      }
    }

    normalizeQuantity(value) {
      const number = Number.parseInt(value, 10);

      if (!Number.isFinite(number) || number < 1) {
        return 1;
      }

      return Math.min(number, 99);
    }

    wrapIndex(index, length) {
      if (!length) return 0;
      return ((index % length) + length) % length;
    }

    formatMoney(cents) {
      if (window.theme?.formatMoney) {
        return window.theme.formatMoney(cents, this.data.moneyFormat);
      }

      return `${(Number(cents || 0) / 100).toFixed(2)} EUR`;
    }

    renderPrice(bracelet) {
      const price = bracelet.priceFormatted || this.formatMoney(bracelet.price || 0);
      const compareAtPrice = Number(bracelet.compareAtPrice || 0);
      const currentPrice = Number(bracelet.price || 0);

      if (compareAtPrice > currentPrice) {
        const compareAt = bracelet.compareAtPriceFormatted || this.formatMoney(compareAtPrice);
        return `<span class="bc__price-sale">${this.escapeHTML(price)}</span><s>${this.escapeHTML(compareAt)}</s>`;
      }

      return this.escapeHTML(price);
    }

    showMessage(message) {
      if (this.els?.message) {
        this.els.message.textContent = message;
      }
    }

    escapeHTML(value) {
      return String(value ?? '').replace(/[&<>"']/g, (character) => {
        const entities = {
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#039;',
        };

        return entities[character];
      });
    }

    escapeAttribute(value) {
      return this.escapeHTML(value);
    }
  }

  customElements.define('bracelet-configurator', BraceletConfigurator);
})();
